import moment from 'moment';
import DynamicDateRanges from 'relevant-shared/reportData/dynamicDateRanges';
import DateUtils from 'relevant-shared/misc/dateUtils';
import { getChartGroupBy, isLineChart } from '../Report/utils';
import PieChartImage from '../../assets/piechart.png';
import BarChartImg from '../../assets/reports/barchart.png';
import BarChartIcon from '../../assets/reports/icons/barchart.png';
import GraphImg from '../../assets/reports/graph.png';
import GraphIcon from '../../assets/reports/icons/graph.png';
import TableImg from '../../assets/reports/table.png';
import TableIcon from '../../assets/reports/icons/table.png';
import ARILogo from '../../assets/icons/modules/RY-module-API.png';
import ARILogoRed from '../../assets/icons/modules/RY-module-API-red.png';
import HBLogo from '../../assets/icons/modules/RY-module-HBA.png';
import HBHLogo from '../../assets/icons/modules/RY-module-HBA-Historical.png';
import NullLogo from '../../assets/icons/modules/null.png';

export function getReportPreviewImage(
	showPieChart,
	showTable,
	showChart,
	groupBy,
) {
	const chartType = groupBy && isLineChart(getChartGroupBy(groupBy)) ? 'line' : 'bar';
	if (showPieChart) {
		return PieChartImage;
	} else if (showChart) {
		if (chartType === 'line') {
			return GraphImg;
		}
		return BarChartImg;
	} else if (showTable) {
		return TableImg;
	}
	return BarChartImg;
}

export function getReportPreviewIcon({ showPieChart, showTable, showChart, groupBy }: {
	showPieChart?: boolean;
	showTable?: boolean;
	showChart?: boolean;
	groupBy?: string;
}) {
	const chartType = groupBy && isLineChart(getChartGroupBy(groupBy)) ? 'line' : 'bar';
	if (showChart) {
		if (chartType === 'line') {
			return GraphIcon;
		}
		return BarChartIcon;
	} else if (showTable) {
		return TableIcon;
	} else {
		return BarChartIcon;
	}
}

export function getReportTypeIcon(type) {
	if (type === 'hb') {
		return HBLogo;
	} else if (type === 'summary_hb') {
		return HBHLogo;
	} else if (type === 'programmatic') {
		return ARILogo;
	} else if (type === 'future') {
		return ARILogoRed;
	} else if (type.startsWith('log_')) {
		return NullLogo;
	} else {
		return NullLogo;
	}
}

export function getReportTypeDescription(type) {
	if (type === 'hb') {
		return 'Real-time header bidding data with 3-second delay for the last 30 days, perfect for immediate technical monitoring.';
	} else if (type === 'summary_hb') {
		return 'Long-term storage of core header bidding metrics and dimensions, retained forever.';
	} else if (type === 'programmatic') {
		return 'Consolidated daily revenue data directly from SSP APIs, ideal for revenue forecasting and final billing statements.';
	} else if (type === 'future') {
		return 'Forecast data for tracking sell-through rates and reserved inventory up to 3 months ahead in your ad server.';
	} else if (type.startsWith('log_')) {
		return null;
	} else {
		return null;
	}
}

export function dateRangeToString(settings: {
	dynamicDateRange?: string;
	start?: number | string | Date;
	end?: number | string | Date;
}) {
	if (settings.dynamicDateRange) {
		const allRanges = DynamicDateRanges.getAllRanges();
		const range = allRanges[settings.dynamicDateRange];
		return { text: range?.name || settings.dynamicDateRange, relative: true };
	}

	// Handle mixed relative/absolute date ranges
	const startIsRelative = DateUtils.isRelativeDate(settings.start);
	const endIsRelative = DateUtils.isRelativeDate(settings.end);

	// For purely relative dates, show a friendly label
	if (startIsRelative && endIsRelative) {
		const start = settings.start as number;
		const end = settings.end as number;

		if (start === 0 && end > 0) {
			return { text: `Next ${end - start + 1} Days (including today)`, relative: true, future: true };
		} else if (start === 1 && end > 0) {
			return { text: `Next ${end - start + 1} Days`, relative: true, future: true };
		} else if (start === 0 && end === 0) {
			return { text: 'Today', relative: true };
		} else if (start === -1 && end === 0) {
			return { text: 'Yesterday and Today', relative: true };
		} else if (start < 0 && end === 0) {
			return { text: `Last ${Math.abs(start)} Days (including today)`, relative: true };
		} else if (start < 0 && end === -1) {
			return { text: `Last ${Math.abs(start)} Days`, relative: true };
		} else if (start < 0 && end < 0) {
			return { text: `${Math.abs(start)} to ${Math.abs(end)} Days ago`, relative: true };
		} else if (start > 0 && end > 0) {
			return { text: `${start} to ${end} Days`, relative: true, future: true };
		}
	}

	// If nothing else, show as a fixed date range
	if (settings.start !== undefined && settings.end !== undefined) {
		const endDate = DateUtils.toDate(settings.start, null, true);
		const startDate = DateUtils.toDate(settings.end, null, true);

		if (startDate && endDate) {
			const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
			try {
				return { text: formatter.formatRange(startDate, endDate), relative: false, future: endDate > new Date() };
			} catch (error) {
				console.error(error);
				console.log(settings);
				return null;
			}
		}
	}

	console.warn('dateRangeToString null for settings', settings);
	return null;
}
